import { type FormEvent, useEffect, useState } from "react";

import { adminApi } from "@/lib/adminApi";
import type { Club } from "@/lib/api";
import { hasWeeklyMeeting } from "@/lib/meeting";
import { ActionRow, Field, PanelSection, StatusBanner } from "@/components/ui";
import { useSaveState } from "@/lib/useSaveState";

const WEEKDAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
] as const;

interface ClubPanelProps {
  readonly onUnauthenticated: () => void;
}

export function ClubPanel({ onUnauthenticated }: ClubPanelProps) {
  const [club, setClub] = useState<Club | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const save = useSaveState(onUnauthenticated);

  useEffect(() => {
    adminApi
      .club()
      .then(setClub)
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  if (loadError) return <StatusBanner tone="error" message={loadError} />;
  if (!club) return <p className="meta opacity-60">Loading…</p>;

  const hasSchedule = hasWeeklyMeeting(club);

  /** Every edit rebuilds the document rather than mutating it in place. */
  const patch = (changes: Partial<Club>) => setClub({ ...club, ...changes });
  const patchMeeting = (changes: Partial<Club["meeting"]>) =>
    patch({ meeting: { ...club.meeting, ...changes } });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const saved = await save.run(
      () => adminApi.saveClub(club),
      () => "Saved. The public site shows this immediately.",
    );
    if (saved) setClub(saved);
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <PanelSection
        title="Weekly meeting"
        description="The recurring session. The site projects the next few dates from this, so the calendar stays current."
      >
        <label className="mb-5 flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--color-ink)]"
            checked={hasSchedule}
            onChange={(event) =>
              // Clearing the slot nulls both halves, so nothing downstream can
              // print half a schedule.
              patchMeeting(
                event.target.checked
                  ? { weekday: 4, weekday_name: "Friday", start_time: "17:00" }
                  : { weekday: null, weekday_name: null, start_time: null },
              )
            }
          />
          <span>
            <span className="font-semibold">We meet on the same day every week</span>
            <span className="mt-0.5 block text-[0.875rem] leading-snug opacity-65">
              Leave this off until the schedule is settled. The site then points people at
              Discord instead of naming a day.
            </span>
          </span>
        </label>

        {hasSchedule ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Day" htmlFor="meeting-weekday">
              <select
                id="meeting-weekday"
                className="input"
                value={club.meeting.weekday ?? 4}
                onChange={(event) => {
                  const weekday = Number(event.target.value);
                  const name = WEEKDAYS[weekday];
                  // The select only offers 0-6; ignore anything else rather
                  // than quietly writing a day nobody picked.
                  if (name) patchMeeting({ weekday, weekday_name: name });
                }}
              >
                {WEEKDAYS.map((name, index) => (
                  <option key={name} value={index}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Start time"
              htmlFor="meeting-time"
              hint="24-hour clock, club local time."
            >
              <input
                id="meeting-time"
                className="input input--mono"
                type="time"
                required
                value={club.meeting.start_time ?? ""}
                onChange={(event) => patchMeeting({ start_time: event.target.value })}
              />
            </Field>
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-4">
          <Field
            label="Where to find us"
            htmlFor="meeting-location"
            hint="Shown on meetings, and used as the default for one-off events."
          >
            <input
              id="meeting-location"
              className="input"
              maxLength={200}
              value={club.meeting.location_note}
              onChange={(event) => patchMeeting({ location_note: event.target.value })}
            />
          </Field>

          <Field label="What happens" htmlFor="meeting-blurb">
            <textarea
              id="meeting-blurb"
              className="input"
              rows={2}
              maxLength={300}
              value={club.meeting.blurb}
              onChange={(event) => patchMeeting({ blurb: event.target.value })}
            />
          </Field>
        </div>
      </PanelSection>

      <PanelSection title="About the club" description="The paragraph on the home page.">
        <div className="flex flex-col gap-4">
          <Field label="Club blurb" htmlFor="club-blurb">
            <textarea
              id="club-blurb"
              className="input"
              rows={3}
              maxLength={600}
              value={club.blurb}
              onChange={(event) => patch({ blurb: event.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Campus" htmlFor="club-campus">
              <input
                id="club-campus"
                className="input"
                value={club.campus}
                onChange={(event) => patch({ campus: event.target.value })}
              />
            </Field>
            <Field label="League" htmlFor="club-league">
              <input
                id="club-league"
                className="input"
                value={club.league.name}
                onChange={(event) =>
                  patch({ league: { ...club.league, name: event.target.value } })
                }
              />
            </Field>
          </div>
        </div>
      </PanelSection>

      <PanelSection title="Links" description="Shown in the Join section and the footer.">
        <div className="flex flex-col gap-4">
          {club.links.map((link, index) => (
            <div key={index} className="grid gap-3 sm:grid-cols-[10rem_1fr]">
              <Field label="Label" htmlFor={`link-label-${index}`}>
                <input
                  id={`link-label-${index}`}
                  className="input"
                  value={link.label}
                  onChange={(event) => {
                    const links = club.links.map((item, i) =>
                      i === index ? { ...item, label: event.target.value } : item,
                    );
                    patch({ links });
                  }}
                />
              </Field>
              <Field label="URL" htmlFor={`link-url-${index}`}>
                <input
                  id={`link-url-${index}`}
                  className="input input--mono"
                  type="url"
                  value={link.url}
                  onChange={(event) => {
                    const links = club.links.map((item, i) =>
                      i === index ? { ...item, url: event.target.value } : item,
                    );
                    patch({ links });
                  }}
                />
              </Field>
            </div>
          ))}
        </div>
      </PanelSection>

      <StatusBanner tone={save.tone} message={save.message} />

      <ActionRow>
        <button type="submit" className="btn" disabled={save.saving}>
          {save.saving ? "Saving…" : "Save club details"}
        </button>
      </ActionRow>
    </form>
  );
}
